import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { SparklesIcon, FlameIcon, ClockIcon, TrophyIcon, UserPlusIcon, CheckCircle2Icon, CheckIcon, SearchIcon, ChevronDownIcon, ArrowUpDownIcon, XIcon } from 'lucide-react';
import { LeadDetailModal, Lead } from './LeadDetailModal';
const SORTS = ['Newest', 'Highest Intent', 'Closest To Purchase', 'Budget High to Low', 'Last Interaction'] as const;
const FILTER_DIMENSIONS = ['Intent Score', 'Budget Range', 'Timeline', 'Location', 'Matched Listing', 'Source', 'Assigned User', 'Date Range'];
const INITIAL_LEADS: Lead[] = [{
  name: 'Ahmed Al Mansoori',
  intentScore: 94,
  lookingFor: 'Toyota Prado',
  budget: 'AED 90,000',
  timeline: 'Within 7 Days',
  location: 'Dubai',
  matchedItem: 'Toyota Prado GXR 2018',
  status: 'Qualified',
  summary: 'Customer strongly prefers GCC vehicles. Not interested in imported vehicles. Ready to purchase this week.',
  phone: '+971 50 123 4567',
  email: 'ahmed@email.com',
  preferredContact: 'WhatsApp',
  preferredLanguage: 'Arabic',
  source: 'Talab AI',
  lastActive: '12 minutes ago',
  receivedDate: 'Jun 6, 2026',
  owner: 'Sara',
  nextAction: 'Call customer',
  nextActionDue: 'Today',
  consent: 'shared'
}, {
  name: 'Sara Khalifa',
  intentScore: 82,
  lookingFor: 'Nissan Patrol',
  budget: 'AED 120,000',
  timeline: 'Within 2 Weeks',
  location: 'Abu Dhabi',
  matchedItem: 'Nissan Patrol 2020',
  status: 'Negotiating',
  summary: 'Comparing 3 SUVs. Asked about financing and warranty. Strong engagement on delivery options.',
  phone: '+971 55 987 6543',
  email: 'sara.k@email.com',
  preferredContact: 'Phone Call',
  preferredLanguage: 'English',
  source: 'Talab AI',
  lastActive: '1 hour ago',
  receivedDate: 'Jun 6, 2026',
  owner: 'Fadi',
  nextAction: 'Send quotation',
  nextActionDue: 'Tomorrow',
  consent: 'shared'
}, {
  name: 'Omar Haddad',
  intentScore: 67,
  lookingFor: 'Family SUV',
  budget: 'AED 70,000 - 85,000',
  timeline: 'This Month',
  location: 'Sharjah',
  matchedItem: 'Toyota Prado GXR 2018',
  status: 'Interested',
  summary: 'Exploring options for a family vehicle. Requested availability confirmation. Budget still flexible.',
  source: 'Talab AI',
  lastActive: '3 hours ago',
  receivedDate: 'Jun 5, 2026',
  owner: 'Unassigned',
  nextAction: 'Follow up tomorrow',
  nextActionDue: 'Tomorrow',
  consent: 'requested'
}, {
  name: 'Anonymous Buyer',
  intentScore: 41,
  lookingFor: 'Compact car',
  budget: 'AED 45,000',
  timeline: 'Undecided',
  location: 'Dubai',
  matchedItem: 'Toyota Yaris 2022',
  status: 'Discovered',
  summary: 'Early-stage research. Asked a single pricing question. No timeline committed yet.',
  source: 'Talab AI',
  lastActive: '2 days ago',
  receivedDate: 'Jun 4, 2026',
  owner: 'Unassigned',
  nextAction: 'Follow up tomorrow',
  consent: 'not_shared'
}];
const PIPELINE = [{
  stage: 'Discovered',
  count: 14,
  color: 'bg-slate-400'
}, {
  stage: 'Qualified',
  count: 9,
  color: 'bg-primary-indigo'
}, {
  stage: 'Interested',
  count: 7,
  color: 'bg-ai-purple'
}, {
  stage: 'Negotiating',
  count: 4,
  color: 'bg-electric-cyan'
}, {
  stage: 'Ready To Buy',
  count: 3,
  color: 'bg-amber-500'
}, {
  stage: 'Won',
  count: 6,
  color: 'bg-green-500'
}, {
  stage: 'Lost',
  count: 2,
  color: 'bg-slate-300'
}] as const;
const STAGE_COUNTS: Record<string, number> = {
  All: 41,
  Discovered: 14,
  Qualified: 9,
  Interested: 7,
  Negotiating: 4,
  'Ready To Buy': 3,
  Won: 6,
  Lost: 2
};
function intentBucket(score: number) {
  if (score >= 90) return {
    label: 'Ready To Buy',
    color: 'text-green-700',
    bg: 'bg-green-100',
    bar: 'bg-green-500'
  };
  if (score >= 75) return {
    label: 'High Intent',
    color: 'text-primary-indigo',
    bg: 'bg-indigo-100',
    bar: 'bg-primary-indigo'
  };
  if (score >= 50) return {
    label: 'Interested',
    color: 'text-amber-600',
    bg: 'bg-amber-100',
    bar: 'bg-amber-500'
  };
  return {
    label: 'Early Stage',
    color: 'text-slate-500',
    bg: 'bg-slate-100',
    bar: 'bg-slate-400'
  };
}
export function BusinessLeads() {
  const [leads, setLeads] = useState<Lead[]>(INITIAL_LEADS);
  const [openLeadIdx, setOpenLeadIdx] = useState<number | null>(null);
  const [stageFilter, setStageFilter] = useState<string>('All');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<string>('Newest');
  const [showStages, setShowStages] = useState(false);
  const [showSort, setShowSort] = useState(false);
  const updateLead = (idx: number, patch: Partial<Lead>) => setLeads(prev => prev.map((l, i) => i === idx ? {
    ...l,
    ...patch
  } : l));
  const filtered = useMemo(() => {
    let list = leads.map((l, idx) => ({
      l,
      idx
    }));
    if (stageFilter !== 'All') list = list.filter(({
      l
    }) => l.status === stageFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(({
        l
      }) => `${l.name} ${l.lookingFor} ${l.matchedItem} ${l.location}`.toLowerCase().includes(q));
    }
    if (sort === 'Highest Intent' || sort === 'Closest To Purchase') list = [...list].sort((a, b) => b.l.intentScore - a.l.intentScore);
    return list;
  }, [leads, stageFilter, search, sort]);
  const STAT_CHIPS = [{
    icon: <UserPlusIcon className="w-4 h-4" data-id="element-669" />,
    label: 'New',
    value: '12',
    accent: 'text-primary-indigo bg-primary-indigo/10'
  }, {
    icon: <CheckCircle2Icon className="w-4 h-4" data-id="element-670" />,
    label: 'Qualified',
    value: '9',
    accent: 'text-ai-purple bg-ai-purple/10'
  }, {
    icon: <FlameIcon className="w-4 h-4" data-id="element-671" />,
    label: 'Hot',
    value: '3',
    accent: 'text-amber-600 bg-amber-100'
  }, {
    icon: <ClockIcon className="w-4 h-4" data-id="element-672" />,
    label: 'Follow-up',
    value: '5',
    accent: 'text-electric-cyan bg-electric-cyan/10'
  }, {
    icon: <TrophyIcon className="w-4 h-4" data-id="element-673" />,
    label: 'Won',
    value: '6',
    accent: 'text-green-600 bg-green-100'
  }];
  const stageColor = stageFilter === 'All' ? 'bg-slate-400' : PIPELINE.find(p => p.stage === stageFilter)?.color ?? 'bg-slate-400';
  return <div className="w-full flex flex-col pb-24" data-id="element-674">
      {/* Summary chips */}
      <div className="flex-shrink-0 flex overflow-x-auto gap-2.5 pb-1 mb-4 -mx-4 px-4 hide-scrollbar" data-id="element-675">
        {STAT_CHIPS.map(s => <div key={s.label} className="flex-shrink-0 flex items-center gap-2.5 bg-white border border-slate-200/70 rounded-2xl pl-2.5 pr-4 py-2.5 shadow-[0_1px_3px_rgb(0,0,0,0.03)]" data-id="element-676">
            <span className={`w-9 h-9 rounded-xl flex items-center justify-center ${s.accent}`} data-id="element-677">
              {s.icon}
            </span>
            <span className="flex flex-col leading-none" data-id="element-678">
              <span className="text-lg font-display font-bold text-dark-navy" data-id="element-679">
                {s.value}
              </span>
              <span className="text-[11px] font-medium text-slate-400 mt-0.5" data-id="element-680">
                {s.label}
              </span>
            </span>
          </div>)}
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} data-id="element-681">
        {/* Search */}
        <div className="relative mb-3" data-id="element-682">
          <SearchIcon className="w-[18px] h-[18px] text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" data-id="element-683" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search leads" className="w-full pl-11 pr-4 py-3.5 bg-white border border-slate-200/80 rounded-2xl text-[15px] text-slate-700 placeholder:text-slate-400 shadow-[0_1px_3px_rgb(0,0,0,0.03)] focus:outline-none focus:ring-2 focus:ring-primary-indigo/15 focus:border-primary-indigo/40 transition-all" data-id="element-684" />
        </div>

        {/* Stage + Sort triggers */}
        <div className="flex items-center gap-2.5 mb-4" data-id="element-685">
          <button onClick={() => setShowStages(true)} className="flex-1 min-w-0 flex items-center gap-2 px-3.5 py-2.5 bg-white border border-slate-200/80 rounded-2xl text-left shadow-[0_1px_3px_rgb(0,0,0,0.03)] active:bg-slate-50 transition-colors" data-id="element-686">
            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${stageColor}`} data-id="element-687" />
            <span className="flex flex-col min-w-0" data-id="element-688">
              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider leading-none mb-0.5" data-id="element-689">
                Stage
              </span>
              <span className="text-[13px] font-semibold text-dark-navy truncate leading-tight" data-id="element-690">
                {stageFilter === 'All' ? 'All stages' : stageFilter}
              </span>
            </span>
            <span className="ml-auto flex items-center gap-1.5 flex-shrink-0" data-id="element-691">
              <span className="text-[11px] font-bold px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-500" data-id="element-692">
                {STAGE_COUNTS[stageFilter] ?? 0}
              </span>
              <ChevronDownIcon className="w-4 h-4 text-slate-400" data-id="element-693" />
            </span>
          </button>
          <button onClick={() => setShowSort(true)} aria-label="Sort leads" className="flex-shrink-0 flex items-center gap-2 px-3.5 py-2.5 bg-white border border-slate-200/80 rounded-2xl shadow-[0_1px_3px_rgb(0,0,0,0.03)] active:bg-slate-50 transition-colors" data-id="element-694">
            <ArrowUpDownIcon className="w-4 h-4 text-slate-400" data-id="element-695" />
            <span className="text-[13px] font-semibold text-dark-navy" data-id="element-696">
              Sort
            </span>
          </button>
        </div>

        {/* Lead list */}
        <div className="space-y-3" data-id="element-697">
          {filtered.map(({
          l,
          idx
        }) => <LeadRow key={idx} lead={l} onOpen={() => setOpenLeadIdx(idx)} data-id="element-698" />)}
          {filtered.length === 0 && <div className="text-center py-20 text-slate-400" data-id="element-699">
              No leads match these filters.
            </div>}
        </div>
      </motion.div>

      <OptionSheet open={showStages} title="Filter by stage" subtitle="Show leads at a specific pipeline stage" onClose={() => setShowStages(false)} options={[{
      value: 'All',
      label: 'All stages',
      count: STAGE_COUNTS['All'],
      color: 'bg-slate-400'
    }, ...PIPELINE.map(p => ({
      value: p.stage,
      label: p.stage,
      count: p.count,
      color: p.color
    }))]} active={stageFilter} onSelect={v => {
      setStageFilter(v);
      setShowStages(false);
    }} data-id="element-700" />

      <OptionSheet open={showSort} title="Sort leads" onClose={() => setShowSort(false)} options={SORTS.map(s => ({
      value: s,
      label: s
    }))} active={sort} onSelect={v => {
      setSort(v);
      setShowSort(false);
    }} data-id="element-701" />

      {openLeadIdx !== null && <LeadDetailModal lead={leads[openLeadIdx]} onClose={() => setOpenLeadIdx(null)} onStatusChange={s => updateLead(openLeadIdx, {
      status: s
    })} onOwnerChange={o => updateLead(openLeadIdx, {
      owner: o
    })} data-id="element-702" />}
    </div>;
}
type SheetOption = {
  value: string;
  label: string;
  count?: number;
  color?: string;
};
function OptionSheet({
  open,
  title,
  subtitle,
  options,
  active,
  onSelect,
  onClose
}: {
  open: boolean;
  title: string;
  subtitle?: string;
  options: SheetOption[];
  active: string;
  onSelect: (v: string) => void;
  onClose: () => void;
}) {
  const mountNode = typeof document !== 'undefined' && document.getElementById('app-screen-root') || (typeof document !== 'undefined' ? document.body : null);
  if (!mountNode) return null;
  return createPortal(<AnimatePresence data-id="element-703">
      {open && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="absolute inset-0 z-[110] flex flex-col justify-end" data-id="element-704">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]" onClick={onClose} data-id="element-705" />
          <motion.div initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 300
      }} className="relative bg-white rounded-t-3xl shadow-2xl flex flex-col max-h-[72%] overflow-hidden" data-id="element-706">
            <div className="flex-shrink-0 pt-3 pb-1 flex justify-center" data-id="element-707">
              <div className="w-10 h-1.5 rounded-full bg-slate-200" data-id="element-708" />
            </div>
            <div className="flex-shrink-0 px-5 pt-2 pb-3 flex items-center justify-between border-b border-slate-100" data-id="element-709">
              <div data-id="element-710">
                <h3 className="text-base font-bold text-dark-navy" data-id="element-711">{title}</h3>
                {subtitle && <p className="text-xs text-slate-400" data-id="element-712">{subtitle}</p>}
              </div>
              <button onClick={onClose} aria-label="Close" className="w-9 h-9 flex items-center justify-center rounded-full bg-slate-100 text-slate-500 active:scale-95 transition-transform" data-id="element-713">
                <XIcon className="w-4 h-4" data-id="element-714" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-3 py-3" data-id="element-715">
              <div className="flex flex-col gap-1" data-id="element-716">
                {options.map(o => {
              const isActive = o.value === active;
              return <button key={o.value} onClick={() => onSelect(o.value)} className={`w-full flex items-center gap-3 px-3 py-3 rounded-2xl text-left transition-colors ${isActive ? 'bg-indigo-50' : 'active:bg-slate-50'}`} data-id="element-717">
                      {o.color && <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${o.color}`} data-id="element-718" />}
                      <span className={`flex-1 text-sm font-semibold truncate ${isActive ? 'text-primary-indigo' : 'text-slate-700'}`} data-id="element-719">
                        {o.label}
                      </span>
                      {typeof o.count === 'number' && <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded-md ${isActive ? 'bg-primary-indigo/10 text-primary-indigo' : 'bg-slate-100 text-slate-500'}`} data-id="element-720">
                          {o.count}
                        </span>}
                      {isActive && <CheckIcon className="w-5 h-5 text-primary-indigo flex-shrink-0" data-id="element-721" />}
                    </button>;
            })}
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>, mountNode);
}
function LeadRow({
  lead,
  onOpen
}: {
  lead: Lead;
  onOpen: () => void;
}) {
  const bucket = intentBucket(lead.intentScore);
  const hasContact = lead.consent === 'shared';
  return <button onClick={onOpen} className="w-full text-left bg-white border border-slate-200/60 rounded-2xl p-4 active:bg-slate-50 transition-all group" data-id="element-722">
      <div className="flex items-start justify-between gap-3 mb-3" data-id="element-723">
        <div className="flex items-center gap-3 min-w-0" data-id="element-724">
          <div className="relative" data-id="element-725">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-indigo to-ai-purple flex items-center justify-center text-white font-semibold text-sm flex-shrink-0" data-id="element-726">
              {lead.name.charAt(0)}
            </div>
            {lead.intentScore >= 90 && <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-amber-400 rounded-full flex items-center justify-center text-[10px]" data-id="element-727">
                🔥
              </span>}
          </div>
          <div className="min-w-0" data-id="element-728">
            <div className="font-semibold text-dark-navy leading-snug truncate" data-id="element-729">
              {lead.name}
            </div>
            <div className="flex items-center gap-1.5 mt-0.5" data-id="element-730">
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${bucket.bg} ${bucket.color}`} data-id="element-731">
                {bucket.label}
              </span>
              {hasContact && <CheckCircle2Icon className="w-3 h-3 text-green-500" data-id="element-732" />}
            </div>
          </div>
        </div>
        <div className="text-right flex-shrink-0" data-id="element-733">
          <div className="text-xl font-display font-bold text-dark-navy leading-none" data-id="element-734">
            {lead.intentScore}
          </div>
          <div className="text-[10px] text-slate-400 font-medium mt-0.5" data-id="element-735">
            Intent
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3 pt-3 border-t border-slate-100" data-id="element-736">
        <Fact label="Looking for" value={lead.lookingFor} data-id="element-737" />
        <Fact label="Budget" value={lead.budget} data-id="element-738" />
      </div>

      <div className="flex items-start gap-2 text-[13px] text-slate-600 leading-snug bg-slate-50 p-2.5 rounded-xl" data-id="element-739">
        <SparklesIcon className="w-4 h-4 text-ai-purple flex-shrink-0" data-id="element-740" />
        <span className="line-clamp-2" data-id="element-741">{lead.summary}</span>
      </div>

      <div className="flex items-center gap-1.5 mt-3 text-[11px] font-medium text-slate-400" data-id="element-742">
        <ClockIcon className="w-3.5 h-3.5 flex-shrink-0" data-id="element-743" />
        <span data-id="element-744">{lead.lastActive}</span>
        {lead.receivedDate && <>
            <span className="w-1 h-1 rounded-full bg-slate-300" data-id="element-745" />
            <span data-id="element-746">{lead.receivedDate}</span>
          </>}
      </div>
    </button>;
}
function Fact({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div className="min-w-0" data-id="element-747">
      <div className="text-[10px] text-slate-400 font-medium uppercase tracking-wider mb-0.5" data-id="element-748">
        {label}
      </div>
      <div className="text-sm font-semibold text-dark-navy truncate" data-id="element-749">
        {value}
      </div>
    </div>;
}